import {
  Button,
  cn,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Stack,
  Text,
} from '@zealot/ui';
import { ChevronLeft } from 'lucide-react';
import type { ReactNode } from 'react';
import { useEffect, useState } from 'react';

import { setMagicToken } from '@/utils/auth';

import { OtpEnterStep } from './OtpEnterStep';
import { OtpSendStep } from './OtpSendStep';
import { OtpStepIcon, type OtpStepIconName } from './OtpStepIcon';

const RESEND_COOLDOWN_SECONDS = 60;

type OtpStep = 'send' | 'enter' | 'success' | 'error';

type StepHeader = {
  title: string;
  description: ReactNode;
  align: 'start' | 'center';
  icon?: OtpStepIconName;
  hasBackButton?: boolean;
};

type StepHeaderContext = {
  email: string;
  error: string;
};

type OtpModalProps = {
  isOpen: boolean;
  onSuccess: () => void;
  onClose?: () => void;
  initialEmail?: string;
  primaryColor?: string;
};

const STEP_HEADERS: Record<OtpStep, (context: StepHeaderContext) => StepHeader> = {
  send: () => ({
    title: 'Verify your email',
    description: "Enter your email and we'll send you a verification code.",
    align: 'start',
  }),
  enter: ({ email }) => ({
    title: 'Enter verification code',
    description: (
      <>
        We sent a 6-digit code to{' '}
        <Text render={<span />} size="sm" weight="medium">
          {email}
        </Text>
      </>
    ),
    align: 'start',
    hasBackButton: true,
  }),
  success: () => ({
    title: 'Verified!',
    description: 'Redirecting you now...',
    align: 'center',
    icon: 'success',
  }),
  error: ({ error }) => ({
    title: 'Verification failed',
    description: error,
    align: 'center',
    icon: 'error',
  }),
};

// gloss
export const OtpModal = ({
  isOpen,
  onSuccess,
  onClose,
  initialEmail = '',
  primaryColor = '#5050d6',
}: OtpModalProps) => {
  const [step, setStep] = useState<OtpStep>('send');
  const [email, setEmail] = useState(initialEmail);
  const [error, setError] = useState('');
  const [lastSentAt, setLastSentAt] = useState<number | null>(null);

  useEffect(() => {
    if (isOpen) {
      setStep('send');
      setEmail(initialEmail);
      setError('');
    }
  }, [isOpen, initialEmail]);

  const handleOtpSent = (sentEmail: string) => {
    setEmail(sentEmail);
    setLastSentAt(Date.now());
    setStep('enter');
  };

  const handleVerified = (token: string) => {
    setMagicToken(token);
    setStep('success');
    onSuccess();
  };

  const handleError = (errorMessage: string) => {
    setError(errorMessage);
    setStep('error');
  };

  const handleRetry = () => {
    setStep('send');
    setError('');
  };

  const isClosable = step !== 'success' && Boolean(onClose);
  const header = STEP_HEADERS[step]({ email, error });

  return (
    <Dialog
      open={isOpen}
      disablePointerDismissal
      onOpenChange={(nextOpen) => {
        if (!nextOpen && isClosable) onClose?.();
      }}
    >
      <DialogContent size="sm" hideClose={!isClosable} className="max-h-[90vh] overflow-y-auto">
        <Stack gap={6}>
          <Stack gap={4} align={header.align}>
            {header.hasBackButton && (
              <Button variant="ghost" size="sm" className="-ml-1.5" onClick={() => setStep('send')}>
                <ChevronLeft data-icon="inline-start" />
                Back
              </Button>
            )}
            {header.icon && <OtpStepIcon icon={header.icon} />}
            <DialogHeader className={cn(header.align === 'center' && 'text-center sm:text-center')}>
              <DialogTitle>{header.title}</DialogTitle>
              <DialogDescription>{header.description}</DialogDescription>
            </DialogHeader>
          </Stack>

          {step === 'send' && (
            <OtpSendStep
              initialEmail={email}
              primaryColor={primaryColor}
              onSent={handleOtpSent}
              onError={handleError}
              lastSentAt={lastSentAt}
              cooldownSeconds={RESEND_COOLDOWN_SECONDS}
            />
          )}
          {step === 'enter' && (
            <OtpEnterStep
              email={email}
              primaryColor={primaryColor}
              onVerified={handleVerified}
              onError={handleError}
              lastSentAt={lastSentAt}
              cooldownSeconds={RESEND_COOLDOWN_SECONDS}
              onResent={() => setLastSentAt(Date.now())}
            />
          )}
          {step === 'error' && (
            <Button className="w-full" style={{ backgroundColor: primaryColor }} onClick={handleRetry}>
              Try again
            </Button>
          )}
        </Stack>
      </DialogContent>
    </Dialog>
  );
};
